import api from '@/services/api'
import { fabric } from 'fabric'
import { nanoid } from 'nanoid'
import { ObjectType } from '@/scenes/engine/common/constants'
import { StaticImageOptions } from '@/scenes/engine/objects/static-image/StaticImage'
import { ImageTextOptions, OpenTypeTextOptions, StaticTextOptions } from '@/scenes/engine/objects'
import { MediaImageRepository } from '@/scenes/engine/objects/media-repository/media_image_repository'
import { MagicDesignTextInfo } from '@/scenes/engine/objects/MagicDesignTextInfo'
import { IFontFamily } from '@/interfaces/editor'
import { MediaFontRepository } from '@/scenes/engine/objects/media-repository/media_font_repository'
import { getFontSizeToMatchRect, measureLineInfoForFont } from '@/scenes/engine/utils/font-loader'
import { MediaImageRepositoryProvider } from '../objects/media-repository/MediaImageRepositoryProvider'
import { Rectangle } from '@/shared/sync/mediaRepository/rectangle'
import { Size } from '@/shared/sync/mediaRepository/size'
import { Point } from '@/shared/sync/mediaRepository/point'
import { MediaImageType } from '@/shared/sync/mediaRepository/media_image_type'
import { DeepSelectionUX } from '../objects/DeepSelectionUX'
import BaseHandler from '../handlers/BaseHandler'
import Handlers from '../handlers'
import FrameHandler from '../handlers/FrameHandler'

class MagicDesignUtils {
  static async breakDesign(editor, image: HTMLImageElement, atIndex: number, fonts: IFontFamily[]) {
    const imageInBase64 = image.src.split(',')[1]
    const layerId = nanoid()
    const layerAssetId = nanoid()
    const imageRepo = MediaImageRepositoryProvider.getEditingProjectInstance()

    const tasks = []
    const textInfoCount = await this.breakText(editor, image, fonts); // Capture the count
    const addImageTask = this.replaceOriginalImages(editor, imageInBase64).then(async imageWithOriginalAssets => {
      const loadedImageWithOriginalAssets = await imageRepo._mediaImageRepositoryProcessing.loadImage(`data:image/png;base64,${imageWithOriginalAssets}`)
      await this.addImageLayerToCanvas(editor, loadedImageWithOriginalAssets, atIndex, layerId, layerAssetId)
    })
    tasks.push(addImageTask)

    tasks.push((async () => {
      const existingImageNoText = await imageRepo.getRecord(layerId, layerAssetId, MediaImageType.originalNoText)
      if (!existingImageNoText) {
        const imageWithoutText = await this.removeTextFromImage(image.src)
        await imageRepo.storeImageBase64(layerId, layerAssetId, MediaImageType.originalNoText, imageWithoutText)
      }
    })())

    await Promise.all(tasks)
    this.selectAndGroupTextObjects(editor, textInfoCount);
  }

  static async replaceOriginalImages(editor, image: string): Promise<string | null> {
    let uploadedImagesLayers = editor.handlers.objectsHandler.getLayers()
      .filter(l => l.type == ObjectType.BAZAART_IMAGE)
      .filter(l => (l as unknown as fabric.StaticImage).isUploadedImage)

    if (uploadedImagesLayers.length == 0) {
      return Promise.resolve(image)
    }
    // @ts-ignore
    let imageRetrievalP = uploadedImagesLayers.map(async l => MediaImageRepositoryProvider.getEditingProjectInstance().getImageBase64(l.id, l.layerAssetStateId, MediaImageType.original))
    return Promise.all(imageRetrievalP).then(uploadedImages => {
      return api.magicDesignImageReplacement(image, uploadedImages).then(replacedImage => {
        return replacedImage
      })
    })
  }

  static async removeTextFromImage(image: string): Promise<string | null> {
    const geminiResponse = await api.gemini('remove all text from this image', image)
    if (!geminiResponse?.success) {
      return null
    }

    // Gemini returns an array of parts, the last part usually contains the image
    const lastPart = geminiResponse?.result?.parts?.at(-1)
    const base64Payload = lastPart?.inlineData?.data
    if (!base64Payload) {
      return null
    }
    return `data:image/png;base64,${base64Payload}`
  }

  static async addImageLayerToCanvas(editor, loadedImage: HTMLImageElement, atIndex: number, guid: string, assetStateId: string): Promise<fabric.StaticImage> {
    let frame = editor.handlers.frameHandler.get()

    let maskInfo = await MediaImageRepositoryProvider.getEditingProjectInstance()._mediaImageRepositoryProcessing.extractMask(loadedImage.src)

    let staticImageOptions: StaticImageOptions = {
      id: guid,
      name: guid,
      subtype: '',
      src: loadedImage.src,
      useBzrtBgMask: false,
      isLatest: false,
      cacheCounter: 0,
      top: frame.top + frame.height / 2,
      left: frame.left + frame.width / 2,
      width: loadedImage.width,
      height: loadedImage.height,
      type: ObjectType.BAZAART_IMAGE,
      isInitializedNormalizeMask: true,
      isInitializedNormalizedImage: true,
      originX: 'center',
      originY: 'center',
      scaleX: frame.width / loadedImage.width,
      scaleY: frame.height / loadedImage.height,
      //@ts-ignore
      isMagicDesignLayer: true,
    }
    let fabricImage = new fabric.StaticImage(loadedImage, staticImageOptions)
    // @ts-ignore
    editor.handlers.canvasHandler.canvas.insertAt(fabricImage, atIndex)

    MediaImageRepositoryProvider.getEditingProjectInstance().storeImageBase64(
      guid,
      assetStateId,
      MediaImageType.latest,
      loadedImage.src
    )

    MediaImageRepositoryProvider.getEditingProjectInstance().storeImageBase64(
      guid,
      assetStateId,
      MediaImageType.original,
      loadedImage.src
    ).then(() => {
      editor.handlers.objectsHandler.setInitializedNormalized(fabricImage as unknown as fabric.Object)
    })
    MediaImageRepositoryProvider.getEditingProjectInstance().storeImageBlobString(
      guid,
      assetStateId,
      MediaImageType.mask,
      maskInfo.blob
    ).then(() => {
      editor.handlers.objectsHandler.setInitializedNormalizeMask(fabricImage as unknown as fabric.Object)
    })

    fabricImage.set('layerAssetStateId', assetStateId)
    return fabricImage
  }

  static async breakText(editor, image: HTMLImageElement, fonts: IFontFamily[]): Promise<number> {
    let frame = editor.handlers.frameHandler.get()

    let imageInBase64 = await MediaImageRepositoryProvider.getEditingProjectInstance()._mediaImageRepositoryProcessing.resizeBase64ToMaxEdgeSize(image.src, 800)
    let resizedImage = await MediaImageRepositoryProvider.getEditingProjectInstance()._mediaImageRepositoryProcessing.loadImage(imageInBase64)

    let textWidthRatio = frame.width / resizedImage.width
    let textHeightRatio = frame.height / resizedImage.height

    imageInBase64 = imageInBase64.split(',')[1]

    const getDestinationInfo = (magicDesignTextInfo: MagicDesignTextInfo, padding: number): {center: Point, size: Size} => {
      let targetWidth = Math.ceil(magicDesignTextInfo.box_coordinates.width * textWidthRatio) ;
      let targetHeight = Math.ceil(magicDesignTextInfo.box_coordinates.height * textHeightRatio);

      let centerX = magicDesignTextInfo.box_coordinates.x * textWidthRatio  + targetWidth / 2
      let centerY = magicDesignTextInfo.box_coordinates.y * textHeightRatio + targetHeight / 2

      let size = new Size(targetWidth + padding, targetHeight + padding)
      let center = new Point(centerX, centerY)
      return { center, size }
    }

    return api.magicDesignTextReplacement(imageInBase64).then(async magicDesignTextInfoList => {
      await Promise.all(magicDesignTextInfoList.map(async (magicDesignTextInfo) => {
        let font = await this.loadFont(magicDesignTextInfo.font_name, fonts)
        if (!font) {
          return
        }

        let fontInfo = await MediaFontRepository.getInstance().loadFontFromUrl(
          font.family,
          font.files['regular'],
          { style: 'normal', weight: '400' }
        )
        if (!fontInfo){
          return
        }
        let guessedNumOfLines = Math.round(magicDesignTextInfo.box_coordinates.height / magicDesignTextInfo.font_height)
        let { center, size} = getDestinationInfo(magicDesignTextInfo, 0);
        let { size: paddedSize } = getDestinationInfo(magicDesignTextInfo, 40);

        let { numOfLines: suggestedNumOfLines, fontSize: suggestedFontSize, lineHeight: suggestedLineHeight } = getFontSizeToMatchRect(
          fontInfo,
          magicDesignTextInfo.box_text,
          size.width,
          size.height,
          {
           lineSpacingRatio: 1,
           charSpacing: 0.1,
           numberOfLines: guessedNumOfLines
          }
        )
        const suggestedFontHeight = measureLineInfoForFont(fontInfo, suggestedFontSize).lineHeight;

        let uniqueId = nanoid()
        let textOption: ImageTextOptions = {
          id: uniqueId,
          arcAngle: 0,
          isPresetText: false,
          bazaartGuid: uniqueId,
          text: magicDesignTextInfo.box_text,
          angle: magicDesignTextInfo.box_angle,
          fontFamily: font?.family ?? 'Poppins-SemiBold',
          name: font?.name ?? 'Aniston',
          left: frame.left + center.x,
          top: frame.top + center.y,
          width: size.width,
          height: size.height,
          textColor: magicDesignTextInfo.text_color,
          type: ObjectType.BAZAART_BREAKTEXT,
          splitByGrapheme: false,
          fontSize: suggestedFontSize,
          fontHeight: suggestedFontHeight,
          originX: 'center',
          originY: 'center',
          scaleX: 1.0,
          scaleY: 1.0,
          textAlign: 'left',
          paintFirst: 'stroke',
          strokeLineJoin: 'round',
          strokeLineCap: 'round',
          lineHeight: suggestedLineHeight,
          isWrapping: true,
          hasRotatingPoint: true,
          originalFontSize: suggestedFontSize,
        }
        if (magicDesignTextInfo.has_outline) {
          textOption.stroke = magicDesignTextInfo.outline_color
          textOption.strokeWidth = magicDesignTextInfo.outline_width
        }
        if (magicDesignTextInfo.has_shadow) {
          let textShadow = new fabric.Shadow({
            color: magicDesignTextInfo.shadow_opacity_class,
            blur: magicDesignTextInfo.shadow_kernel_class,
            offsetX: magicDesignTextInfo.shadow_amplitude * Math.cos(magicDesignTextInfo.shadow_direction_class),
            offsetY: magicDesignTextInfo.shadow_amplitude * Math.sin(magicDesignTextInfo.shadow_direction_class)
          })
          textOption.shadow = textShadow
        }

        const textElement = new fabric.ImageText(textOption)
        textElement.initDimensions()

        let { numOfLines, fontSize, lineHeight: actualLineHeightNoSpacing } = getFontSizeToMatchRect(
          fontInfo,
          magicDesignTextInfo.box_text,
          textElement.width,
          textElement.height,
          {
           lineSpacingRatio: 1,
           charSpacing: 0.1 ,
           numberOfLines: guessedNumOfLines
          })

        const rect = new fabric.Rect({
          ...textOption,
          fill: '',            // No fill (transparent)
          stroke: 'red',       // Red border
          strokeWidth: 15,      // Border thickness
          selectable: true,
          width: paddedSize.width,
          height : paddedSize.height,
          originX: 'center',
          originY: 'center',
          lockMovementX: true,
          lockMovementY: true,
          excludeFromExport: true
        })
        textOption.fontSize = fontSize
        rect._set('additionalInfo', textOption)
        editor.handlers.canvasHandler.canvas.add(rect)
      }))
      return magicDesignTextInfoList.length; // Return the length of the list
    })
  }

  static async loadFont(fontName: string, fonts: IFontFamily[]): Promise<IFontFamily | null> {
    const normalizeFontName = (name) => {
      return name.replace('-', '').replace('_', '').toLowerCase()
    }

    const normalizeFontNameWithoutWeightInfo = (fullFontName) => {
      let fontNameWithoutWeight = normalizeFontName(fullFontName)
        .replace('semibold', '')
        .replace('bold', '')
        .replace('regular', '')
        .replace('italic', '')
      return fontNameWithoutWeight
    }

    let normalizedFontName = normalizeFontName(fontName)
    let normalizedFontNameWithoutWeightInfo = normalizeFontNameWithoutWeightInfo(fontName)

    return new Promise((resolve) => {
      let foundFont = null
      let foundFontWithoutWeight = null
      for (let font of fonts) {
        let normalizedFontFamily = normalizeFontName(font.family)
        let normalizedFontFamilyWithoutWeightInfo = normalizeFontNameWithoutWeightInfo(font.family)

        if (normalizedFontFamily == normalizedFontName) {
          foundFont = font
        }
        if (normalizedFontFamilyWithoutWeightInfo == normalizedFontNameWithoutWeightInfo) {
          foundFontWithoutWeight = font
        }
      }

      let font = foundFont ?? foundFontWithoutWeight
      if (font) {
        const fontFile = font.files['regular' as any]
        const fontOpt = {
          name: font.name,
          family: font.family,
          url: fontFile,
          options: { style: 'normal', weight: '400' },
        }
        MediaFontRepository.getInstance()
          .loadFontFromUrl(fontOpt.family, fontOpt.url, fontOpt.options)
          .then(() => resolve(font))
          .catch(err => {
            console.log(err)
            resolve(null)
          })
      } else {
        resolve(null)
      }
    })
  }

  static async cropTextFromImage(object: any, boundingBox: Rectangle) {
    let layerId = object.id
    let currentAssetStateId = object.layerAssetStateId
    let newAssetStateId = nanoid()

    let originalSrc = await MediaImageRepositoryProvider.getEditingProjectInstance().getImage(layerId, currentAssetStateId, MediaImageType.original)
    let original = await MediaImageRepositoryProvider.getEditingProjectInstance()._mediaImageRepositoryProcessing.loadImage(originalSrc)

    let originalNoTextSrc = await MediaImageRepositoryProvider.getEditingProjectInstance().getImage(layerId, currentAssetStateId, MediaImageType.originalNoText)
    let originalNoText = await MediaImageRepositoryProvider.getEditingProjectInstance()._mediaImageRepositoryProcessing.loadImage(originalNoTextSrc)

    let absolueBoundingBox = boundingBox.multiply(new Size(originalNoText.width, originalNoText.height));

    let originalNoTextPortionMaskSrc = await MediaImageRepositoryProvider.getEditingProjectInstance()._mediaImageRepositoryProcessing.createColorImage('#000000', absolueBoundingBox.width, absolueBoundingBox.height);
    let originalNoTextPortionMask = await MediaImageRepositoryProvider.getEditingProjectInstance()._mediaImageRepositoryProcessing.loadImage(originalNoTextPortionMaskSrc)

    let paddedMask = await MediaImageRepositoryProvider.getEditingProjectInstance()._mediaImageRepositoryProcessing.padImage(originalNoTextPortionMask, new Size(originalNoText.width, originalNoText.height,), absolueBoundingBox, "#ffffff");
    let greyMask = await MediaImageRepositoryProvider.getEditingProjectInstance()._mediaImageRepositoryProcessing.extractGreyMaskFromColorImage(paddedMask.src);

    let originalNoTextPortionSrc = await MediaImageRepositoryProvider.getEditingProjectInstance()._mediaImageRepositoryProcessing.applyMaskToImage(originalNoText, greyMask);
    let originalNoTextPortion = await MediaImageRepositoryProvider.getEditingProjectInstance()._mediaImageRepositoryProcessing.loadImage(originalNoTextPortionSrc);

    // let topLeft = new Point(absolueBoundingBox.x, absolueBoundingBox.y);
    let topLeft = new Point(0, 0);
    let newOriginal = await MediaImageRepositoryProvider.getEditingProjectInstance()._mediaImageRepositoryProcessing.composite(original, originalNoTextPortion, topLeft, true)

    await MediaImageRepositoryProvider.getEditingProjectInstance().storeImageBase64(layerId, newAssetStateId, MediaImageType.original, newOriginal.src)

    let mask = await MediaImageRepositoryProvider.getEditingProjectInstance().getImage(layerId, currentAssetStateId, MediaImageType.mask)
    await MediaImageRepositoryProvider.getEditingProjectInstance().storeImageBlobString(layerId, newAssetStateId, MediaImageType.mask, mask)
    await MediaImageRepositoryProvider.getEditingProjectInstance().storeImageBlobString(layerId, newAssetStateId, MediaImageType.originalNoText, originalNoTextSrc)

    let fullBoundingBox = new Rectangle(0, 0, original.width, original.height)
    let latestImageInfo = await MediaImageRepositoryProvider.getEditingProjectInstance().generateLatestImageInfo(layerId, newAssetStateId, null, fullBoundingBox)
    await MediaImageRepositoryProvider.getEditingProjectInstance().storeLatestImageInfo(layerId, newAssetStateId, latestImageInfo)

    object.set('layerAssetStateId', newAssetStateId)

    const dataURL = latestImageInfo.latestImage.toDataURL()
    await object.replaceImage(dataURL, true)
  }

  static async convertToStaticText(canvas: fabric.Canvas, handler: Handlers, isDelete: boolean) {
    const activeGroup = DeepSelectionUX.getInstance().selectedParent
    const activeObject = DeepSelectionUX.getInstance().selectedChild

    if (!activeGroup) { return }
    const { transactionHandler, frameHandler, objectsHandler } = handler
    let end = transactionHandler.save();
    // 1) Remember z-index of the group
    const groupIndex = canvas.getObjects().indexOf(activeGroup)

    // 2. Ungroup -> ActiveSelection
    const activeSelection = objectsHandler.ungroup(activeGroup, false, false)

    // Snapshot children
    const all = activeSelection.getObjects().slice()
    // @ts-ignore
    const remaining = all.filter(o => o.bazaartGuid !== activeObject.bazaartGuid)
    let lastImageObject = remaining.find(o => o.type == ObjectType.BAZAART_IMAGE) || null;

    canvas.discardActiveObject()
    
    remaining.forEach((o, index) => canvas.moveTo(o, groupIndex + index))
    if(isDelete) {
      canvas.remove(activeObject)
      if(remaining.length > 1) {
        const selection = new fabric.ActiveSelection(remaining, { canvas: canvas })
        const group = objectsHandler.group(selection)
        group.set({
          type: ObjectType.BAZAART_GROUP_BREAKTEXT
        })
        canvas.moveTo(group, groupIndex)
      } else {
        canvas.moveTo(lastImageObject, groupIndex)
      }
      
      canvas.requestRenderAll()
      end()
      return
    }

    // Remove the group id from the active object to prevent re-grouping
    // @ts-ignore
    activeObject.set({groupId: null})

    if (!lastImageObject) {
      canvas.requestRenderAll()
      end()
      return
    }

    let frame = frameHandler.get()
    const width = activeObject.getScaledWidth();
    const height = activeObject.getScaledHeight();

    let rect = new Rectangle(
      (activeObject.left - activeObject.width / 2 - frame.left) / frame.width,
      (activeObject.top - activeObject.height / 2 - frame.top) / frame.height,
      width / frame.width,
      height / frame.height
    )
    
    await this.cropTextFromImage(lastImageObject, rect)

    // Add the cropped text to the canvas
    //@ts-ignore
    let additionalInfo = activeObject.get('additionalInfo');
    let uniqueId = nanoid()
    let textOption: OpenTypeTextOptions = {
      id: uniqueId,
      arcAngle: 0,
      isPresetText: false,
      bazaartGuid: uniqueId,
      text: additionalInfo.text,
      angle: activeObject.angle,
      fontFamily: additionalInfo.fontFamily,
      name: activeObject.name,
      left: activeObject.left,
      top: activeObject.top,
      width: activeObject.width,
      height: activeObject.height,
      fontHeight: additionalInfo.fontHeight,
      fill: additionalInfo.textColor,
      type: ObjectType.BAZAART_TEXT,
      splitByGrapheme: false,
      originalFontSize: additionalInfo.fontSize,
      fontSize: additionalInfo.fontSize,
      lineHeight: 1,
      originX: 'center',
      originY: 'center',
      scaleX: 1.0,
      scaleY: 1.0,
      textAlign: 'left',
      paintFirst: 'stroke',
      strokeLineJoin: 'round',
      strokeLineCap: 'round',
      isWrapping: true,
      hasRotatingPoint: true,
    }
    const textElement = new fabric.StaticText(textOption)
    canvas.add(textElement)
    textElement.setCoords()
    textElement.set({
      // @ts-ignore
      _originalWidth: textElement.width,
      // @ts-ignore
      _originalHeight: textElement.height,
      hasRotatingPoint: true,
      selectable: true,
      hasControls: true,
      isResized: true
    })
    canvas.remove(activeObject)

    // Re-group imageText and latest lastImageObject
    if(remaining.length > 1) {
      const selection = new fabric.ActiveSelection(remaining, { canvas: canvas })
      const group = objectsHandler.group(selection)
      group.set({
        type: ObjectType.BAZAART_GROUP_BREAKTEXT
      })
      canvas.bringToFront(textElement)
      canvas.moveTo(group, groupIndex)
      canvas.setActiveObject(textElement)
    } else {
      canvas.moveTo(lastImageObject, groupIndex)
    }

    canvas.requestRenderAll()
    end()
  }

  /**
   * Convert fabric.ImageFromText to fabric.StaticText
   * @param refObject - the object to convert
   * @returns the converted text object
   */
  static async convertImageFromTextToText(canvas: fabric.Canvas, frame: fabric.Frame, refObject): Promise<fabric.StaticText> {
    const activeObject = refObject ? refObject : canvas.getActiveObject()
    if (!activeObject) { return }

    let font = MediaFontRepository.getInstance().get(refObject.textMetadata.fontFamily) ?? MediaFontRepository.getInstance().defaultFont;
    let { numOfLines , fontSize, lineHeight } = getFontSizeToMatchRect(
        font,
        refObject.textMetadata.text,
        activeObject.getScaledWidth(),
        activeObject.getScaledHeight(),
        {
          lineSpacingRatio: refObject.textMetadata.lineHeight,
          charSpacing: refObject.textMetadata.charSpacing / 1000
        }
    )
    let fontHeight = measureLineInfoForFont(font, fontSize).lineHeight;
    const padding = activeObject.backgroundColor ? 10 : 0;
    let textOption = {
      left: activeObject.left,
      top: activeObject.top,
      width: activeObject.getScaledWidth() + padding * 2,
      height: lineHeight * numOfLines,
      scaleX: 1,
      scaleY: 1,
      originX: 'center',
      originY: 'center',
      flipX: activeObject.flipX,
      flipY: activeObject.flipY,
      paintFirst: 'stroke',
      fontStyle: 'normal',
      strokeLineJoin: 'round',
      strokeLineCap: 'round',
      angle: activeObject.angle,
      fill: activeObject.fill,
      originalFontSize: fontSize,
      backgroundColor: activeObject.backgroundColor,
      bazaartGuid: activeObject.bazaartGuid,
      id: activeObject.id,
      type: ObjectType.BAZAART_TEXT,
      sizeOnCanvas: activeObject.sizeOnCanvas,
      // @ts-ignore
      ...activeObject.textMetadata,
      fontSize,
      fontHeight,
    }
    textOption.lineHeight = (lineHeight + padding * 2) / fontHeight;

    const textElement = new fabric.OpenTypeText(textOption)
    const textAttr = activeObject.textProperties.attributedText.runs[0].attributes
    let fontInfo = MediaFontRepository.getInstance().get(textAttr.NSOriginalFont?.systemName ?? textAttr.NSFont.systemName);
    textElement.set({
      // @ts-ignore
      _originalWidth: textElement.width,
      // @ts-ignore
      _originalHeight: textElement.height,
      hasRotatingPoint: true,
      selectable: true,
      hasControls: true,
      isResized: true,
      convertedFromImage: true,
    })
    textElement.clipPath = frame
    
    textElement.setFontInfo(fontInfo, textElement.fontFamily);
    canvas.add(textElement)
    canvas.setActiveObject(textElement)
    textElement.setCoords()

    canvas.remove(activeObject)
    canvas.renderAll()
    // @ts-ignore
    return textElement
  }

  static selectAndGroupTextObjects(editor, textInfoCount: number) {
    if (textInfoCount > 0) {
      // Get the last `textInfoCount` objects added to the canvas
      const allObjects = editor.handlers.canvasHandler.canvas.getObjects();
      const lastTextObjects = allObjects.slice(-(textInfoCount + 1));
      const selection = new fabric.ActiveSelection(lastTextObjects, { canvas: editor.handlers.canvasHandler.canvas });
      const group = editor.handlers.objectsHandler.group(selection)
      group.set({
        type: ObjectType.BAZAART_GROUP_BREAKTEXT
      })
      
      // @ts-ignore
      // editor.handlers.canvasHandler.canvas.setActiveObject(selection);
      // editor.handlers.canvasHandler.canvas.requestRenderAll();
      // editor.handlers.objectsHandler.group();
    }
  }
}

export default MagicDesignUtils

